import { TNativeSdkConfigurations } from '../../../@pano-project/types/IGateway';

// PRX-24667: a "native-SDK method" is paid through the provider's own web SDK
// (Revolut Pay) instead of the regular Pay button. This is a sibling of the
// Google Pay / Apple Pay predicate (`isDigitalWallet`), which stays unchanged.

export const REVOLUT_PAY_PROVIDER = 'revolut_pay';

// Providers this Cashier can drive. The method key of the list equals the
// provider name, which is how a method is still known when its details did
// not load.
export const SUPPORTED_NATIVE_SDK_PROVIDERS: readonly string[] = [REVOLUT_PAY_PROVIDER];

export const NATIVE_SDK_MODES = {
  Prod: 'prod',
  Sandbox: 'sandbox',
} as const;

type TNativeSdkGatewayLike =
  | {
      payment_method_key?: string | null;
      native_sdk_configurations?: TNativeSdkConfigurations | null;
    }
  | null
  | undefined;

export const isSupportedNativeSdkProvider = (provider?: string | null): boolean =>
  !!provider && SUPPORTED_NATIVE_SDK_PROVIDERS.includes(provider);

/**
 * The native SDK object of a gateway, or null when the gateway has none or
 * names a provider this Cashier does not support.
 */
export const getNativeSdkConfigurations = (
  gateway: TNativeSdkGatewayLike,
): TNativeSdkConfigurations | null => {
  const configurations = gateway?.native_sdk_configurations;

  return configurations && isSupportedNativeSdkProvider(configurations.provider)
    ? configurations
    : null;
};

export const isNativeSdkMethodKey = (key?: string | null): boolean =>
  isSupportedNativeSdkProvider(key);

/**
 * "This is a native-SDK method": the details carry the object with a supported
 * provider, or the method key is a supported provider (details did not load).
 */
export const isNativeSdkMethod = (gateway: TNativeSdkGatewayLike): boolean =>
  !!getNativeSdkConfigurations(gateway) || isNativeSdkMethodKey(gateway?.payment_method_key);

/**
 * The provider of a native-SDK method, taken from the details or, when they
 * did not load, from the method key. Null for any other method.
 */
export const getNativeSdkProvider = (gateway: TNativeSdkGatewayLike): string | null => {
  const configurations = getNativeSdkConfigurations(gateway);
  if (configurations) return configurations.provider;

  return isNativeSdkMethodKey(gateway?.payment_method_key)
    ? (gateway?.payment_method_key as string)
    : null;
};

/**
 * The full form of the object: the public key and a known mode are present, so
 * the SDK script can be loaded. The "marker" (provider and gateway id only)
 * and a missing object are not full.
 */
export const hasNativeSdkParameters = (
  configurations?: TNativeSdkConfigurations | null,
): configurations is TNativeSdkConfigurations & {
  public_api_key: string;
  mode: 'prod' | 'sandbox';
} =>
  !!configurations &&
  isSupportedNativeSdkProvider(configurations.provider) &&
  !!configurations.public_api_key &&
  (configurations.mode === NATIVE_SDK_MODES.Prod ||
    configurations.mode === NATIVE_SDK_MODES.Sandbox);

// Store readers for the selected gateway.
export const selectCurrentNativeSdkConfigurations = (state: any): TNativeSdkConfigurations | null =>
  getNativeSdkConfigurations(state?.currentGateway);

export const selectIsNativeSdkMethodSelected = (state: any): boolean =>
  isNativeSdkMethod(state?.currentGateway);
